import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import BackButton from '../components/BackButton';
import { CITIES } from '../data/localLandingData';
import { getServicesData } from '../data/servicesData';
import { useTranslation } from 'react-i18next';

const SERVICES_META = [
  { slug: 'beton-imprime', label: 'Béton Imprimé', color: 'text-amber-400' },
  { slug: 'beton-desactive', label: 'Béton Désactivé', color: 'text-emerald-400' },
  { slug: 'beton-bale', label: 'Béton Balayé', color: 'text-blue-400' },
  { slug: 'beton-lisse', label: 'Béton Lissé', color: 'text-violet-400' },
  { slug: 'beton-cire', label: 'Béton Ciré', color: 'text-rose-400' },
];

const DEPTS = [
  { name: 'Vaucluse (84)', filter: 'Vaucluse (84)' },
  { name: 'Gard (30)', filter: 'Gard (30)' },
  { name: 'Drôme (26)', filter: 'Drôme (26)' },
  { name: 'Bouches-du-Rhône (13)', filter: 'Bouches-du-Rhône (13)' },
];

const ZonesIntervention = () => {
  const { i18n } = useTranslation();
  const services = getServicesData(i18n.language);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Zones d'Intervention - Béton Décoratif Vaucluse, Gard, Drôme, PACA | DumaExpertBéton"
        description="DumaExpertBéton intervient dans 27 villes du Vaucluse, Gard, Drôme et Bouches-du-Rhône. Béton imprimé, désactivé, balayé, lissé et ciré. Devis gratuit."
        url="https://duma-expert-beton.fr/zones-intervention"
      />

      <div className="bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-5xl">
          <BackButton />
          <div className="mt-6">
            <div className="flex items-center gap-3 mb-3">
              <MapPin className="w-7 h-7 text-amber-400" />
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold" data-testid="zones-h1">
                Nos Zones d'Intervention
              </h1>
            </div>
            <p className="text-slate-300 text-lg max-w-3xl mt-3">
              Nous intervenons dans {CITIES.length} villes réparties sur 4 départements : Vaucluse, Gard, Drôme et Bouches-du-Rhône. Découvrez nos services de béton décoratif près de chez vous.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-12">
        {/* Par département */}
        {DEPTS.map(dept => {
          const deptCities = CITIES.filter(c => c.dept === dept.filter);
          return (
            <section key={dept.name} className="mb-12" data-testid={`dept-${dept.filter}`}>
              <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-200">
                {dept.name} — {deptCities.length} villes
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {deptCities.map(city => (
                  <div key={city.slug} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-slate-900 text-lg">{city.name}</h3>
                        <p className="text-xs text-slate-500">{city.cp} {city.distance > 0 ? `· ${city.distance} km` : '· Siège social'}</p>
                      </div>
                      <MapPin className="w-5 h-5 text-slate-300" />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {SERVICES_META.map(svc => (
                        <Link
                          key={svc.slug}
                          to={`/${svc.slug}-${city.slug}`}
                          className={`text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors`}
                          data-testid={`zone-${svc.slug}-${city.slug}`}
                        >
                          {svc.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        {/* Matrice services × villes */}
        <section className="mt-12 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Tous nos services par ville</h2>
          {SERVICES_META.map(svc => (
            <div key={svc.slug} className="mb-6">
              <h3 className={`font-semibold text-base mb-2 ${svc.color.replace('text-', 'text-')}`} style={{color: undefined}}>
                <span className="text-slate-800">{svc.label}</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {CITIES.map(city => (
                  <Link
                    key={city.slug}
                    to={`/${svc.slug}-${city.slug}`}
                    className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg transition-colors"
                  >
                    {city.name}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* CTA */}
        <div className="bg-slate-900 rounded-xl p-8 text-center text-white mt-8">
          <h2 className="text-2xl font-bold mb-3">Votre ville n'est pas listée ?</h2>
          <p className="text-slate-300 mb-6 max-w-lg mx-auto">
            Nous intervenons dans un rayon de 65 km autour de Sainte-Cécile-les-Vignes. Contactez-nous pour savoir si votre projet est dans notre zone.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-6 py-2.5 rounded-lg transition-colors">
              Devis Gratuit <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 px-6 py-2.5 rounded-lg transition-colors">
              Nous Contacter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZonesIntervention;
